import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import {
  CircularProgress,
  Chip,
  FormControlLabel,
  Switch,
  Divider,
  Alert,
  MenuItem,
} from "@mui/material";
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

function parseProviderRequest(req) {
  if (!req) return req
  if (typeof req.body !== 'string') return req
  try {
    return Object.assign({}, req, { body: JSON.parse(req.body) })
  } catch (e) {
    return req
  }
}

export default function TestSmsChannelDialog(props) {
  var open = props.open;
  var channels = props.channels || [];
  var defaultChannel = props.defaultChannel;
  var testLoading = props.testLoading;
  var testResult = props.testResult;
  var testError = props.testError;

  var [phone, setPhone] = React.useState("");
  var [message, setMessage] = React.useState(
    "Test SMS from Aspin — please ignore."
  );
  var [channelName, setChannelName] = React.useState(
    defaultChannel || ""
  );
  var [dryRun, setDryRun] = React.useState(false);

  React.useEffect(
    function() {
      if (open) {
        setPhone("");
        setMessage("Test SMS from Aspin — please ignore.");
        setChannelName(defaultChannel || "");
        setDryRun(false);
        props.onReset();
      }
    },
    [open]
  );

  function handleSubmit() {
    var payload = {
      phone: phone,
      message: message,
      dry_run: dryRun,
    };
    if (channelName) payload.channel_name = channelName;
    props.onTest(payload);
  }

  function renderResult() {
    if (!testResult && !testError) return null;

    if (testError) {
      return (
        <Alert severity="error" sx={{ mt: 2 }}>
          {typeof testError === "string"
            ? testError
            : JSON.stringify(testError, null, 2)}
        </Alert>
      );
    }

    var isSuccess = testResult && testResult.success;

    return (
      <Box mt={2}>
        <Divider sx={{ mb: 2 }} />
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          {isSuccess ? (
            <CheckCircleIcon color="success" />
          ) : (
            <CancelIcon color="error" />
          )}
          <Typography variant="subtitle2">
            {isSuccess ? "Test sent successfully" : "Test failed"}
          </Typography>
          {testResult.channel_used && (
            <Chip
              size="small"
              label={"Channel: " + testResult.channel_used}
              variant="outlined"
            />
          )}
          {testResult.message_id && (
            <Chip
              size="small"
              label={"MsgID: " + testResult.message_id}
              color="primary"
              variant="outlined"
            />
          )}
        </Box>

        {testResult.request_id && (
          <Typography variant="caption" color="text.secondary" display="block">
            Request ID: {testResult.request_id}
          </Typography>
        )}

        {testResult.error && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {testResult.error}
          </Alert>
        )}

        {testResult.provider_request && (
          <Box mt={1}>
            <Typography variant="caption" color="text.secondary">
              Provider Request:
            </Typography>
            <Box
              component="pre"
              sx={{
                background: "#f5f5f5",
                borderRadius: 1,
                p: 1,
                fontSize: "0.72rem",
                fontFamily: "monospace",
                overflowX: "auto",
                maxHeight: 160,
                mt: 0.5,
              }}
            >
              {JSON.stringify(
                parseProviderRequest(testResult.provider_request),
                null,
                2
              )}
            </Box>
          </Box>
        )}

        {testResult.provider_response && (
          <Box mt={1}>
            <Typography variant="caption" color="text.secondary">
              Provider Response:
            </Typography>
            <Box
              component="pre"
              sx={{
                background: "#f5f5f5",
                borderRadius: 1,
                p: 1,
                fontSize: "0.72rem",
                fontFamily: "monospace",
                overflowX: "auto",
                maxHeight: 160,
                mt: 0.5,
              }}
            >
              {JSON.stringify(testResult.provider_response, null, 2)}
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Dialog open={open} fullWidth maxWidth="sm" onClose={props.onClose}>
      <DialogTitle>
        Test SMS Channel
      </DialogTitle>

      <DialogContent>
        <ValidatorForm onSubmit={handleSubmit}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {channels.length > 1 && (
              <Grid item xs={12}>
                <TextValidator
                  select
                  fullWidth
                  label="Channel"
                  value={channelName}
                  onChange={function(e) { setChannelName(e.target.value); }}
                  variant="outlined"
                >
                  <MenuItem value="">
                    <em>Auto-resolve</em>
                  </MenuItem>
                  {channels.map(function(c) {
                    var name = c.channel_name || "(default)";
                    return (
                      <MenuItem key={name} value={c.channel_name || ""}>
                        {name}
                      </MenuItem>
                    );
                  })}
                </TextValidator>
              </Grid>
            )}

            <Grid item xs={12}>
              <TextValidator
                fullWidth
                label="Recipient Phone Number"
                value={phone}
                onChange={function(e) { setPhone(e.target.value); }}
                variant="outlined"
                validators={["required"]}
                errorMessages={["Phone number is required"]}
                helperText="International format preferred, e.g. +260971234567"
              />
            </Grid>

            <Grid item xs={12}>
              <TextValidator
                fullWidth
                multiline
                rows={3}
                label="Message"
                value={message}
                onChange={function(e) { setMessage(e.target.value); }}
                variant="outlined"
                validators={["required"]}
                errorMessages={["Message is required"]}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={dryRun}
                    onChange={function(e) { setDryRun(e.target.checked); }}
                    color="primary"
                  />
                }
                label="Dry run (validate config without sending)"
              />
            </Grid>
          </Grid>
        </ValidatorForm>

        {testLoading && (
          <Box display="flex" alignItems="center" gap={2} mt={2}>
            <CircularProgress size={20} />
            <Typography variant="body2">Sending test SMS...</Typography>
          </Box>
        )}

        {renderResult()}
      </DialogContent>

      <DialogActions>
        <Button onClick={props.onClose} color="inherit">
          Close
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={testLoading}
          startIcon={testLoading ? <CircularProgress size={16} /> : null}
        >
          {dryRun ? "Dry Run" : "Send Test"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
